import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    McIcon,
    McInput,
    McNotification,
    McLoadingIndicator,
    McButton,
} from '@maersk-global/mds-react-wrapper';
import { McTooltip } from '@maersk-global/mds-react-wrapper/components-core/mc-tooltip';
import { DownloadResultsButton } from '../../Common/DownloadResultsButton';
import { ExplainPlanCard } from '../../Common/ExplainPlanCard';
import { ChartVisualization } from '../../Common/ChartVisualization';
import { QueryParameterValue } from '../../../utils/queryUtils';

type BottomTab = 'results' | 'params' | 'explain' | 'visualization';
type LayoutMode = 'vertical' | 'horizontal';

interface QueryOverviewTabProps {
    query: { queryId: string };
    sqlContent: string;
    baseSqlContent?: string;
    isOverride?: boolean;
    terminalId?: string;
    onCopy: (text: string) => void;
    executionParameters: QueryParameterValue[];
    onParameterChange: (index: number, value: string) => void;
    isExecuting: boolean;
    executionResult: any;
    executionError: string | null;
    planLines?: string[];
    isExplainingPlan?: boolean;
    aiExplanation?: string | null;
    isGeneratingAiExplanation?: boolean;
    validationErrors?: string[];
    onAiExplain?: () => void;
    onCloseExplainPlan?: () => void;
    aiModelOptions?: { label: string; value: string }[];
    selectedModel?: string;
    onModelChange?: (model: string) => void;
    onExecute?: () => void;
    onExplain?: () => void;
    canExecute?: boolean;
    canExplain?: boolean;
    onToggleSchemaPanel?: () => void;
    schemaPanelOpen?: boolean;
}

export const QueryOverviewTab: React.FC<QueryOverviewTabProps> = ({
    query,
    sqlContent,
    baseSqlContent,
    isOverride = false,
    terminalId,
    onCopy,
    executionParameters,
    onParameterChange,
    isExecuting,
    executionResult,
    executionError,
    planLines = [],
    isExplainingPlan = false,
    aiExplanation = null,
    isGeneratingAiExplanation = false,
    validationErrors = [],
    onAiExplain,
    onCloseExplainPlan,
    aiModelOptions,
    selectedModel,
    onModelChange,
    onExecute,
    onExplain,
    canExecute = false,
    canExplain = false,
    onToggleSchemaPanel,
    schemaPanelOpen = false,
}) => {
    const [layoutMode, setLayoutMode] = useState<LayoutMode>('vertical');
    const [splitPos, setSplitPos] = useState(280);
    const [bottomTab, setBottomTab] = useState<BottomTab>('results');
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef({ pos: 0, splitPos: 0 });

    const switchLayout = useCallback((mode: LayoutMode) => {
        setLayoutMode(mode);
        setSplitPos(mode === 'vertical' ? 280 : Math.round(window.innerWidth * 0.45));
    }, []);

    const handleDragStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        dragStartRef.current = {
            pos: layoutMode === 'vertical' ? e.clientY : e.clientX,
            splitPos,
        };
    }, [layoutMode, splitPos]);

    useEffect(() => {
        if (!isDragging) return;
        const handleMove = (e: MouseEvent) => {
            if (layoutMode === 'vertical') {
                const delta = dragStartRef.current.pos - e.clientY;
                setSplitPos(Math.max(80, Math.min(window.innerHeight * 0.65, dragStartRef.current.splitPos + delta)));
            } else {
                const delta = e.clientX - dragStartRef.current.pos;
                setSplitPos(Math.max(300, Math.min(window.innerWidth * 0.75, dragStartRef.current.splitPos + delta)));
            }
        };
        const handleUp = () => setIsDragging(false);
        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleUp);
        return () => {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);
        };
    }, [isDragging, layoutMode]);

    useEffect(() => { if (executionResult) setBottomTab('results'); }, [executionResult]);
    useEffect(() => { if (isExplainingPlan || planLines.length > 0) setBottomTab('explain'); }, [isExplainingPlan, planLines.length]);

    const hasParams = executionParameters.length > 0;
    const hasExplainContent = isExplainingPlan || planLines.length > 0 || !!aiExplanation || validationErrors.length > 0;

        // ── SQL pane ──
    const sqlPane = (
        <div style={{
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            ...(layoutMode === 'vertical'
                ? { flex: 1, minHeight: 120 }
                : { width: splitPos, minWidth: 300, flexShrink: 0 }),
        }}>
            <div style={{
                display: 'flex', alignItems: 'center', height: 40, padding: '0 16px',
                borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb', flexShrink: 0,
            }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#374151' }}>
                    <McIcon icon="file" style={{ fontSize: 14, color: '#0073ab' }} />
                    SQL Content
                    {isOverride && terminalId && (
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            backgroundColor: '#f59e0b', color: '#fff',
                            fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                            padding: '2px 8px', borderRadius: 999,
                        }}>⚡ {terminalId}</span>
                    )}
                </span>
            </div>
            <pre style={{
                flex: 1, overflow: 'auto', margin: 0, padding: '18px 22px',
                backgroundColor: '#1e1e2e', color: '#cdd6f4',
                fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
                fontSize: '13px', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
                {sqlContent}
            </pre>
        </div>
    );

    // ── Drag handle (direction-aware) ──
    const dragHandle = (
        <div
            onMouseDown={handleDragStart}
            style={layoutMode === 'vertical' ? {
                height: 8, cursor: 'ns-resize', flexShrink: 0,
                backgroundColor: isDragging ? '#d1d5db' : '#e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            } : {
                width: 6, cursor: 'ew-resize', flexShrink: 0,
                backgroundColor: isDragging ? '#d1d5db' : '#e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
        >
            {layoutMode === 'vertical'
                ? <div style={{ width: 40, height: 3, borderRadius: 3, backgroundColor: '#9ca3af' }} />
                : <div style={{ width: 3, height: 40, borderRadius: 3, backgroundColor: '#9ca3af' }} />}
        </div>
    );

    // ── Results / bottom pane ──
    const bottomPane = (
        <div style={{
            display: 'flex', flexDirection: 'column', backgroundColor: '#fff', overflow: 'hidden',
            ...(layoutMode === 'vertical'
                ? { height: splitPos, flexShrink: 0 }
                : { flex: 1, minWidth: 0 }),
        }}>
            <div style={{
                display: 'flex', alignItems: 'stretch', borderBottom: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb', flexShrink: 0, height: 38, paddingLeft: 8,
            }}>
                {(['results', ...(hasParams ? ['params'] as BottomTab[] : []), ...(hasExplainContent ? ['explain'] as BottomTab[] : []), ...(executionResult?.data?.length ? ['visualization'] as BottomTab[] : [])] as BottomTab[]).map(tab => (
                    <button key={tab} onClick={() => setBottomTab(tab)} style={{
                        border: 'none', borderBottom: bottomTab === tab ? '2px solid #0073ab' : '2px solid transparent',
                        background: 'none', cursor: 'pointer', padding: '0 16px', fontSize: 13,
                        fontWeight: bottomTab === tab ? 600 : 400,
                        color: bottomTab === tab ? '#0073ab' : '#6b7280',
                        height: '100%', transition: 'color 0.15s, border-color 0.15s',
                        outline: 'none', whiteSpace: 'nowrap',
                    }}>
                        {tab === 'results' ? 'Results' : tab === 'params' ? 'Parameters' : tab === 'visualization' ? 'Visualization' : 'Explain Plan'}
                    </button>
                ))}
                {executionResult?.data?.length > 0 && bottomTab === 'results' && (
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, paddingRight: 12 }}>
                        <span style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5 }}>
                            <McIcon icon="check-circle" style={{ color: '#16a34a', fontSize: 14 }} />
                            Showing {Math.min(executionResult.data.length, 100).toLocaleString()} of {Number(executionResult.resultCount).toLocaleString()} rows
                        </span>
                        <DownloadResultsButton data={executionResult.data} queryId={query.queryId} sqlQuery={sqlContent} />
                    </div>
                )}
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
                {bottomTab === 'results' && (
                    <>
                        {executionError && (
                            <McNotification appearance="error" heading="Execution Error" body={executionError} style={{ marginBottom: 10 }} />
                        )}
                        {!executionResult && !isExecuting && !executionError && (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
                                <McIcon icon="play-circle" style={{ fontSize: 36, opacity: 0.3, display: 'block', margin: '0 auto 10px' }} />
                                <span style={{ fontSize: '0.85rem' }}>Select a terminal and run Execute to see results</span>
                            </div>
                        )}
                        {isExecuting && !executionResult && (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                <McLoadingIndicator fit="small" />
                                <p style={{ fontSize: '0.85rem', color: '#9ca3af', margin: '12px 0 0' }}>Executing query…</p>
                            </div>
                        )}
                        {executionResult?.data?.length > 0 && (
                            <div style={{ animation: 'fadeIn 0.3s ease-in', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                                {(() => {
                                    const columns = Object.keys(executionResult.data[0]);
                                    return (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', fontFamily: 'inherit' }}>
                                            <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f5f7f9' }}>
                                                <tr>
                                                    {columns.map(k => (
                                                        <th key={k} style={{
                                                            padding: '10px 14px', textAlign: 'left', fontWeight: 700,
                                                            fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px',
                                                            color: '#6b7280', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap',
                                                        }}>{k}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {executionResult.data.slice(0, 100).map((row: any, idx: number) => (
                                                    <tr key={idx} style={{
                                                        borderBottom: '1px solid #f3f4f6',
                                                        backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa',
                                                    }}>
                                                        {columns.map(k => (
                                                            <td key={k} style={{ padding: '9px 14px', whiteSpace: 'nowrap', color: '#374151' }}>
                                                                {row[k] !== null && row[k] !== undefined ? String(row[k]) : ''}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    );
                                })()}
                            </div>
                        )}
                        {executionResult && executionResult.data?.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
                                <McIcon icon="shield" style={{ fontSize: 32, opacity: 0.3, display: 'block', margin: '0 auto 8px' }} />
                                <span style={{ fontSize: '0.85rem' }}>Query executed but returned no results.</span>
                            </div>
                        )}
                    </>
                )}

                {bottomTab === 'visualization' && (
                    <ChartVisualization data={executionResult?.data || []} />
                )}

                {bottomTab === 'params' && (
                    <div>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 10 }}>
                            Input Parameters
                        </span>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                            {executionParameters.map((param, idx) => (
                                <McInput
                                    key={param.name}
                                    label={param.name}
                                    value={param.value as string}
                                    input={(e: any) => onParameterChange(idx, e.target.value)}
                                    fit="small"
                                />
                            ))}
                        </div>
                    </div>
                )}

                {bottomTab === 'explain' && (
                    <ExplainPlanCard
                        planLines={planLines}
                        isExplaining={isExplainingPlan}
                        aiExplanation={aiExplanation}
                        isGeneratingAiExplanation={isGeneratingAiExplanation}
                        validationErrors={validationErrors}
                        onAiExplain={onAiExplain}
                        onClose={onCloseExplainPlan}
                        aiModelOptions={aiModelOptions}
                        selectedModel={selectedModel}
                        onModelChange={onModelChange}
                    />
                )}
            </div>
        </div>
    );

    // ── Right icon sidebar (40 px strip) ──
    const sidebar = (
        <div style={{
            width: 40, flexShrink: 0, borderLeft: '1px solid #e5e7eb', backgroundColor: '#f9fafb',
            display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0', gap: 2,
        }}>
            {onToggleSchemaPanel && (
                <>
                    <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="left-center">
                        <div slot="trigger">
                            <McButton variant={schemaPanelOpen ? 'filled' : 'plain'} appearance={schemaPanelOpen ? 'primary' : 'secondary'} fit="small"
                                onClick={onToggleSchemaPanel} style={{ minWidth: 36, padding: '0 8px' }} aria-pressed={schemaPanelOpen}>
                                <McIcon icon="database" />
                            </McButton>
                        </div>
                        <span>{schemaPanelOpen ? 'Hide schema panel' : 'Show schema panel'}</span>
                    </McTooltip>
                    <hr style={{ width: 24, border: 'none', borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
                </>
            )}
            <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="left-center">
                <div slot="trigger">
                    <McButton variant="plain" appearance="secondary" fit="small" 
                        onClick={() => onCopy(sqlContent)} style={{ minWidth: 36, padding: '0 8px' }}>
                        <McIcon icon="copy" />
                    </McButton>
                </div>
                <span>Copy SQL</span>
            </McTooltip>

            <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="left-center">
                <div slot="trigger">
                    <McButton variant="plain" appearance="secondary" fit="small" 
                        onClick={() => {
                            const blob = new Blob([sqlContent], { type: 'text/plain;charset=utf-8;' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url; a.download = `${query.queryId}.sql`;
                            document.body.appendChild(a); a.click();
                            document.body.removeChild(a); URL.revokeObjectURL(url);
                        }} style={{ minWidth: 36, padding: '0 8px' }}>
                        <McIcon icon="tray-arrow-down" />
                    </McButton>
                </div>
                <span>Download SQL</span>
            </McTooltip>

            <hr style={{ width: 24, border: 'none', borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />

            <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="left-center">
                <div slot="trigger">
                    <McButton variant="plain" appearance="primary" fit="small" 
                        disabled={!canExecute}
                        onClick={canExecute && !isExecuting ? onExecute : undefined} style={{ minWidth: 36, padding: '0 8px' }}>
                        {isExecuting ? <McLoadingIndicator fit="small" /> : <McIcon icon="paper-plane" />}
                    </McButton>
                </div>
                <span>{isExecuting ? 'Running…' : 'Execute'}</span>
            </McTooltip>

            <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="left-center">
                <div slot="trigger">
                    <McButton variant="plain" appearance="secondary" fit="small" 
                        disabled={!canExplain}
                        onClick={canExplain ? onExplain : undefined} style={{ minWidth: 36, padding: '0 8px' }}>
                        <McIcon icon="lightning" />
                    </McButton>
                </div>
                <span>Explain Plan</span>
            </McTooltip>

            <hr style={{ width: 24, border: 'none', borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />

            <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="left-center">
                <div slot="trigger">
                    <McButton variant={layoutMode === 'vertical' ? 'outlined' : 'plain'} appearance="secondary" fit="small" 
                        onClick={() => switchLayout('vertical')} style={{ minWidth: 36, padding: '0 8px' }}>
                        <McIcon icon="chevron-down" />
                    </McButton>
                </div>
                <span>Top / Bottom split</span>
            </McTooltip>

            <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="left-center">
                <div slot="trigger">
                    <McButton variant={layoutMode === 'horizontal' ? 'outlined' : 'plain'} appearance="secondary" fit="small" 
                        onClick={() => switchLayout('horizontal')} style={{ minWidth: 36, padding: '0 8px' }}>
                        <McIcon icon="chevron-right" />
                    </McButton>
                </div>
                <span>Left / Right split</span>
            </McTooltip>
        </div>
    );

    return (
        <div style={{
            display: 'flex', flexDirection: 'row',
            height: 'calc(100vh - 200px)', minHeight: 0,
            userSelect: isDragging ? 'none' : 'auto',
        }}>
            <div style={{
                flex: 1, minWidth: 0, overflow: 'hidden',
                display: 'flex',
                flexDirection: layoutMode === 'vertical' ? 'column' : 'row',
            }}>
                {sqlPane}
                {dragHandle}
                {bottomPane}
            </div>
            {sidebar}
            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            ` }} />
        </div>
    );
};
